// AdminFeedbackResponses.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import {
  fetchAllKontrolScoreFeedbackForAdmin,
  fetchKontrolScoreFeedbackQuestions,
  fetchManagerViewerByAuthUserId,
  getCurrentUser,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import { SECTION_ORDER, labelForOption, labelForQuestion } from "./ScoreFeedback";
import { aggregateFeedbackQuestion } from "./utils/feedbackAggregation";
import type { KontrolScoreFeedbackAdminRow, KontrolScoreFeedbackQuestion } from "../types/scores";

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        display: "grid",
        placeItems: "center",
        color: "#111827",
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

const thSx = {
  color: "#6B7280",
  fontWeight: 900,
  bgcolor: "#F9FAFB",
  borderBottom: "1px solid #E5E7EB",
} as const;

const SECTION_LABELS: Record<string, { en: string; de: string }> = {
  start: { en: "Start", de: "Start" },
  questionnaire: { en: "Questionnaire", de: "Fragebogen" },
  result: { en: "Result", de: "Ergebnis" },
  recommendation: { en: "Recommendation", de: "Empfehlung" },
  overall: { en: "Overall", de: "Insgesamt" },
};

function employeeFullName(row: KontrolScoreFeedbackAdminRow) {
  const name = `${row.employees?.first_name ?? ""} ${row.employees?.last_name ?? ""}`.trim();
  return name || "—";
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

// Shared by AdminFeedbackResponses and AdminEventManagement (per-participant "view feedback").
export function FeedbackAnswersBySection(props: {
  answers: Record<string, string | string[]> | null | undefined;
  questions: KontrolScoreFeedbackQuestion[];
  lang: "en" | "de";
  L: (en: string, de: string) => string;
}) {
  const { answers, questions, lang, L } = props;

  const questionsBySection = React.useMemo(() => {
    const grouped = new Map<string, KontrolScoreFeedbackQuestion[]>();
    for (const q of questions) {
      const list = grouped.get(q.section_key) ?? [];
      list.push(q);
      grouped.set(q.section_key, list);
    }
    Array.from(grouped.values()).forEach((list) => {
      list.sort((a, b) => a.order_index - b.order_index);
    });
    return grouped;
  }, [questions]);

  const sections = SECTION_ORDER.map((sectionKey) => {
    const sectionQuestions = (questionsBySection.get(sectionKey) ?? []).filter(
      (q) => answers?.[q.question_code] !== undefined
    );
    return { sectionKey, sectionQuestions };
  }).filter((s) => s.sectionQuestions.length > 0);

  if (sections.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#6B7280" }}>
        {L("No feedback submitted yet.", "Noch kein Feedback eingereicht.")}
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      {sections.map(({ sectionKey, sectionQuestions }) => {
        const label = SECTION_LABELS[sectionKey] ?? { en: sectionKey, de: sectionKey };

        return (
          <Box key={sectionKey}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280" }}
            >
              {L(label.en, label.de)}
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              {sectionQuestions.map((question) => (
                <Box key={question.id} sx={{ borderRadius: 2, border: "1px solid #E5E7EB", bgcolor: "#F9FAFB", p: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                    {labelForQuestion(question, lang)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#374151", mt: 0.5 }}>
                    {answerDisplay(question, answers?.[question.question_code], lang)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function answerDisplay(
  question: KontrolScoreFeedbackQuestion,
  value: string | string[] | undefined,
  lang: "en" | "de"
) {
  if (value === undefined || value === null || value === "") return "—";

  if (question.input_type === "text") {
    return String(value);
  }

  const values = Array.isArray(value) ? value : [value];
  return values
    .map((v) => {
      const option = question.answer_options.find((o) => o.value === v);
      return option ? labelForOption(option, lang) : v;
    })
    .join(", ");
}

export default function AdminFeedbackResponses(props: { handlePageChange: (page: string) => void }) {
  void props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (typedLang === "de" ? de : en), [typedLang]);

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<KontrolScoreFeedbackAdminRow[]>([]);
  const [questions, setQuestions] = React.useState<KontrolScoreFeedbackQuestion[]>([]);
  const [search, setSearch] = React.useState("");
  const [detailTarget, setDetailTarget] = React.useState<KontrolScoreFeedbackAdminRow | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setUiError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr) throw userErr;
        if (!userData?.user) {
          throw new Error(L("Not authenticated.", "Nicht angemeldet."));
        }

        const { data: me, error: meErr } = await fetchManagerViewerByAuthUserId(userData.user.id);
        if (meErr) throw meErr;
        if (!me?.id || String(me.company_role) !== "admin") {
          throw new Error(L("You do not have admin access.", "Du hast keinen Admin-Zugriff."));
        }

        const [{ data: feedbackRows, error: feedbackErr }, { data: questionRows, error: questionsErr }] =
          await Promise.all([fetchAllKontrolScoreFeedbackForAdmin(), fetchKontrolScoreFeedbackQuestions()]);

        if (feedbackErr) throw feedbackErr;
        if (questionsErr) throw questionsErr;
        if (!mounted) return;

        setRows((feedbackRows ?? []) as KontrolScoreFeedbackAdminRow[]);
        setQuestions((questionRows ?? []) as KontrolScoreFeedbackQuestion[]);
      } catch (err) {
        if (!mounted) return;
        setUiError(
          err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen.")
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [L]);

  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const name = employeeFullName(row).toLowerCase();
      const email = (row.employees?.company_email ?? "").toLowerCase();
      const company = (row.employees?.companies?.name ?? "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [rows, search]);

  const overviewAggregates = React.useMemo(() => {
    if (questions.length === 0 || rows.length === 0) return [];
    const allAnswers = rows.map((row) => row.answers ?? {});
    return [...questions]
      .sort((a, b) => a.order_index - b.order_index)
      .map((question) => aggregateFeedbackQuestion(question, allAnswers, typedLang, L));
  }, [questions, rows, typedLang, L]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <RateReviewOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("Feedback Responses", "Feedback-Antworten")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Every question response submitted through the KONTROL score feedback flow.",
                  "Alle Antworten, die über den KONTROL-Score-Feedback-Ablauf abgegeben wurden."
                )}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${L("Submissions", "Einreichungen")}: ${loading ? "…" : rows.length}`}
            variant="outlined"
            sx={{
              height: 42,
              borderRadius: 2.5,
              borderColor: "#D1D5DB",
              bgcolor: "#FFFFFF",
              color: "#111827",
              fontWeight: 900,
              px: 1,
              minWidth: 140,
              justifyContent: "center",
            }}
          />
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        {!loading && overviewAggregates.length > 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <SectionIconBox>
                  <BarChartOutlinedIcon fontSize="small" />
                </SectionIconBox>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                    {L("Overview", "Übersicht")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    {L(
                      "Average answer per question, across every submission.",
                      "Durchschnittliche Antwort pro Frage, über alle Einreichungen hinweg."
                    )}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {overviewAggregates.map((aggregate) => (
                  <Box
                    key={aggregate.question.id}
                    sx={{ borderRadius: 2.5, border: "1px solid #E5E7EB", bgcolor: "#F9FAFB", p: 1.5 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#111827",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {labelForQuestion(aggregate.question, typedLang)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 700, whiteSpace: "nowrap" }}>
                        n={aggregate.respondentCount}
                      </Typography>
                    </Stack>

                    {aggregate.scalePct !== null ? (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={aggregate.scalePct}
                          sx={{
                            height: 6,
                            borderRadius: 20,
                            bgcolor: "#E5E7EB",
                            "& .MuiLinearProgress-bar": { bgcolor: "#111827", borderRadius: 20 },
                          }}
                        />
                      </Box>
                    ) : null}

                    <Typography variant="caption" sx={{ color: "#374151", display: "block", mt: 1 }}>
                      {aggregate.conclusion}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {L("Submitted feedback", "Eingereichtes Feedback")}
              </Typography>

              <TextField
                size="small"
                placeholder={L("Search by name, email or company", "Suche nach Name, E-Mail oder Unternehmen")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              />
            </Stack>

            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : filteredRows.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6B7280", py: 2 }}>
                {L("No feedback submitted yet.", "Noch kein Feedback eingereicht.")}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>{L("Name", "Name")}</TableCell>
                      <TableCell sx={thSx}>{L("Email", "E-Mail")}</TableCell>
                      <TableCell sx={thSx}>{L("Company", "Unternehmen")}</TableCell>
                      <TableCell sx={thSx}>{L("Answers", "Antworten")}</TableCell>
                      <TableCell sx={thSx}>{L("Submitted", "Eingereicht")}</TableCell>
                      <TableCell sx={thSx} align="right">
                        {L("Actions", "Aktionen")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => (
                      <TableRow key={row.employee_id} hover>
                        <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{employeeFullName(row)}</TableCell>
                        <TableCell sx={{ color: "#374151" }}>{row.employees?.company_email ?? "—"}</TableCell>
                        <TableCell sx={{ color: "#374151" }}>{row.employees?.companies?.name ?? "—"}</TableCell>
                        <TableCell sx={{ color: "#374151" }}>{Object.keys(row.answers ?? {}).length}</TableCell>
                        <TableCell sx={{ color: "#374151" }}>{formatDate(row.submitted_at)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={L("View responses", "Antworten ansehen")}>
                            <IconButton size="small" onClick={() => setDetailTarget(row)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={!!detailTarget} onClose={() => setDetailTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            {L("Feedback responses", "Feedback-Antworten")}
            {detailTarget ? (
              <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 400 }}>
                {employeeFullName(detailTarget)}
              </Typography>
            ) : null}
          </Box>
          <IconButton size="small" onClick={() => setDetailTarget(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailTarget ? (
            <FeedbackAnswersBySection
              answers={detailTarget.answers}
              questions={questions}
              lang={typedLang}
              L={L}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
